import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCw, X, Sparkles, Download } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const UpdateNotification = () => {
  const [showUpdate, setShowUpdate] = useState(false);
  const [registration, setRegistration] = useState(null);
  const [showFullModal, setShowFullModal] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    // Listen for service worker update events
    const handleSwUpdate = (event) => {
      console.log('Service worker update detected');
      setRegistration(event.detail);
      setShowUpdate(true);
      // Show full modal for major updates
      setShowFullModal(true);
    };

    // Listen for service worker messages
    const handleSwMessage = (event) => {
      if (event.data && event.data.type === 'SW_UPDATED') {
        toast.success('App updated!', {
          description: event.data.message || 'Twin Tube is now up to date.',
          duration: 4000,
        });
        // Reload to apply changes including new icon
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    };

    window.addEventListener('swUpdate', handleSwUpdate);
    
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleSwMessage);
      
      // Check if there's already a waiting worker
      navigator.serviceWorker.ready.then((reg) => {
        if (reg.waiting) {
          setRegistration(reg);
          setShowUpdate(true);
          setShowFullModal(true);
        }
        
        // Listen for new service worker updates
        reg.addEventListener('updatefound', () => {
          const newWorker = reg.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // New update available
                setRegistration(reg);
                setShowUpdate(true);
                setShowFullModal(true);
              }
            });
          }
        });
      });

      // Check for updates periodically
      const checkForUpdates = () => {
        navigator.serviceWorker.ready.then((reg) => {
          reg.update().catch(console.error);
        });
      };
      
      // Check every 30 minutes
      const updateInterval = setInterval(checkForUpdates, 30 * 60 * 1000);
      
      return () => {
        clearInterval(updateInterval);
        window.removeEventListener('swUpdate', handleSwUpdate);
        navigator.serviceWorker.removeEventListener('message', handleSwMessage);
      };
    }

    return () => {
      window.removeEventListener('swUpdate', handleSwUpdate);
    };
  }, []);

  const handleUpdate = async () => {
    setIsUpdating(true);
    
    try {
      if (registration && registration.waiting) {
        // Tell the waiting service worker to activate
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        
        toast.loading('Updating Twin Tube...', {
          id: 'updating',
          duration: 3000,
        });
        
        // Wait a moment then reload
        setTimeout(() => {
          toast.dismiss('updating');
          toast.success('Update complete!', {
            description: 'Reloading with new features and icon...',
            duration: 2000,
          });
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        }, 1500);
      } else {
        // No waiting worker, just reload
        window.location.reload();
      }
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Update failed', {
        description: 'Please refresh the page manually.',
      });
      setIsUpdating(false);
    }
    
    setShowUpdate(false);
    setShowFullModal(false);
  };

  const handleDismiss = () => {
    setShowUpdate(false);
    setShowFullModal(false);
  };

  if (!showUpdate) return null;

  // Full modal for major updates
  if (showFullModal) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            className="relative overflow-hidden border border-purple-500/50 rounded-2xl max-w-sm w-full shadow-2xl"
          >
            {/* Background */}
            <div 
              className="absolute inset-0 bg-cover bg-center opacity-20"
              style={{ backgroundImage: 'url(/images/twin-tube-bg.jpg)' }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-purple-900/95 via-gray-900/98 to-gray-900" />
            
            {/* Content */}
            <div className="relative p-6">
              <button
                onClick={handleDismiss}
                className="absolute top-4 right-4 text-gray-400 hover:text-white p-1"
              >
                <X size={20} />
              </button>
              
              <div className="text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.1, type: "spring" }}
                  className="w-16 h-16 mx-auto mb-4 rounded-2xl overflow-hidden border-2 border-purple-500/50 shadow-lg"
                >
                  <img 
                    src="/icons/icon-192x192.png" 
                    alt="Twin Tube" 
                    className="w-full h-full object-cover"
                  />
                </motion.div>
                
                <h2 className="text-xl font-bold text-white mb-2 flex items-center justify-center gap-2">
                  <Sparkles className="text-yellow-400" size={20} />
                  Update Available!
                </h2>
                
                <p className="text-gray-300 text-sm mb-4">
                  A new version of Twin Tube is ready with:
                </p>
                
                <ul className="text-left text-sm text-gray-300 space-y-2 mb-6 bg-black/30 rounded-lg p-3">
                  <li className="flex items-center gap-2">
                    <span className="text-purple-400">✨</span>
                    New app icon design
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-blue-400">🎨</span>
                    Updated branding
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-400">🔧</span>
                    Bug fixes & improvements
                  </li>
                </ul>
                
                <div className="space-y-2">
                  <Button
                    onClick={handleUpdate}
                    disabled={isUpdating}
                    className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
                  >
                    {isUpdating ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Updating...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Update Now
                      </>
                    )}
                  </Button>
                  
                  <button
                    onClick={handleDismiss}
                    className="text-sm text-gray-400 hover:text-white"
                  >
                    Remind me later
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // Simple banner notification
  return (
    <motion.div
      initial={{ opacity: 0, y: -50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -50 }}
      className="fixed top-4 left-1/2 -translate-x-1/2 z-[9999]"
    >
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-3">
        <RefreshCw className="w-5 h-5 animate-spin" />
        <span className="text-sm font-medium">A new version is available!</span>
        <Button
          onClick={handleUpdate}
          size="sm"
          className="bg-white text-purple-600 hover:bg-gray-100 h-7 px-3"
        >
          Update Now
        </Button>
        <button
          onClick={handleDismiss}
          className="text-white/80 hover:text-white p-1"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
};

export default UpdateNotification;
